import React, { useEffect, useRef } from 'react';
import { Heading, Text } from './ui';
import SignedImage from './SignedImage';

const RecipeCardOverlay = ({ recipe, onClose }) => {
    const overlayRef = useRef(null);

    // Enter fullscreen when component mounts
    useEffect(() => {
        const enterFullscreen = async () => {
            if (overlayRef.current) {
                try {
                    await overlayRef.current.requestFullscreen();
                } catch (error) {
                    console.log('Error entering fullscreen:', error);
                }
            }
        };
        enterFullscreen();

        // Listen for fullscreen changes
        const handleFullscreenChange = () => {
            if (!document.fullscreenElement) {
                onClose();
            }
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
        };
    }, [onClose]);

    // Exit fullscreen when component unmounts
    useEffect(() => {
        return () => {
            if (document.fullscreenElement) {
                document.exitFullscreen().catch(() => {});
            }
        };
    }, []);

    const colorTokens = {
        alabaster: '#EEEEE7',
        white: '#FFFFFF',
        oxfordBlue: '#1b263b',
        richBlack: '#0d1b2a',
        notecardBlue: '#4A90E2',
        red: '#DC2626',
        neutral: {
            0: '#FFFFFF',
            50: '#F9F8F6',
            900: '#0D0D0D'
        }
    };

    const formatCookingTime = (minutes) => {
        if (!minutes) return null;
        if (minutes < 60) return `${minutes} min`;
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    };

    // Parse components and assembly if they're JSON strings
    const parsedRecipe = { ...recipe };
    if (typeof recipe.components === 'string') {
        try {
            parsedRecipe.components = JSON.parse(recipe.components);
        } catch (e) {
            console.error('Error parsing components:', e);
            parsedRecipe.components = null;
        }
    }
    if (typeof recipe.assembly === 'string') {
        try {
            parsedRecipe.assembly = JSON.parse(recipe.assembly);
        } catch (e) {
            console.error('Error parsing assembly:', e);
            parsedRecipe.assembly = null;
        }
    }

    // Helper to combine multi-component data with headers
    const combineMultiComponentData = (recipe) => {
        if (!recipe.isMultiComponent || !recipe.components || recipe.components.length === 0) {
            return null;
        }

        const combinedIngredients = [];
        const combinedSteps = [];

        // Add all component ingredients with headers
        recipe.components.forEach((component) => {
            if (component.ingredients && Array.isArray(component.ingredients) && component.ingredients.length > 0) {
                const validIngredients = component.ingredients.filter(i => {
                    const ing = typeof i === 'string' ? i : (i?.name || i?.text || String(i));
                    return ing && ing.trim && ing.trim().length > 0;
                });
                if (validIngredients.length > 0) {
                    combinedIngredients.push({
                        type: 'header',
                        text: `For the ${component.name}:`
                    });
                    validIngredients.forEach(ing => {
                        const ingText = typeof ing === 'string' ? ing : (ing?.name || ing?.text || String(ing));
                        combinedIngredients.push({
                            type: 'item',
                            text: ingText
                        });
                    });
                }
            }
        });

        // Add all component steps with headers
        recipe.components.forEach((component) => {
            if (component.steps && component.steps.length > 0) {
                const validSteps = component.steps
                    .map(step => typeof step === 'string' ? step : step?.text || '')
                    .filter(s => s && s.trim());
                if (validSteps.length > 0) {
                    combinedSteps.push({
                        type: 'header',
                        text: `For the ${component.name}:`
                    });
                    validSteps.forEach(step => {
                        combinedSteps.push({
                            type: 'item',
                            text: step
                        });
                    });
                }
            }
        });

        // Add assembly steps at the end (without header)
        if (recipe.assembly && recipe.assembly.steps && recipe.assembly.steps.length > 0) {
            const validAssemblySteps = recipe.assembly.steps
                .map(step => typeof step === 'string' ? step : step?.text || '')
                .filter(s => s && s.trim());
            validAssemblySteps.forEach(step => {
                combinedSteps.push({
                    type: 'item',
                    text: step
                });
            });
        }

        return {
            ingredients: combinedIngredients,
            steps: combinedSteps
        };
    };

    // Get combined data for multi-component recipes
    const combinedData = combineMultiComponentData(parsedRecipe);
    
    // Parse ingredients - use combined data if multi-component, otherwise use simple format
    const ingredients = combinedData 
        ? combinedData.ingredients
        : (Array.isArray(parsedRecipe.ingredients)
            ? parsedRecipe.ingredients
            : parsedRecipe.ingredients?.items || []);

    // Parse instructions - use combined data if multi-component, otherwise use simple format
    const instructions = parsedRecipe.instructions;
    const instructionSteps = combinedData
        ? combinedData.steps
        : (Array.isArray(instructions)
            ? instructions
            : instructions?.steps || []);

    // Always use 2 columns for ingredients in recipe cards
    const ingredientColumns = 2;
    
    // Calculate columns for instructions based on content length
    const getInstructionColumns = () => {
        const stepCount = instructionSteps.filter(s => s.type !== 'header').length;
        if (stepCount <= 6) return 2;
        if (stepCount <= 12) return 3;
        return 4;
    };

    const instructionColumns = getInstructionColumns();

    const handleClose = async () => {
        if (document.fullscreenElement) {
            try {
                await document.exitFullscreen();
            } catch (error) {
                console.log('Error exiting fullscreen:', error);
            }
        }
        onClose();
    };

    return (
        <div
            ref={overlayRef}
            className="fixed z-50 bg-neutral-50 pointer-events-auto"
            style={{
                width: '100vw',
                height: '100vh',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                position: 'fixed'
            }}
            onClick={handleClose}
        >
            <div
                className="w-full h-full bg-neutral-0 hairline relative overflow-hidden p-6"
                style={{ width: '100%', height: '100%' }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Close Button */}
                <button
                    onClick={handleClose}
                    className="absolute top-4 right-4 z-10 w-8 h-8 bg-neutral-0 hairline flex items-center justify-center text-neutral-900 hover:bg-neutral-900 hover:text-neutral-0 transition-all duration-120 font-mono text-lg p-0 m-0"
                >
                    ✕
                </button>

                {/* Main Grid Layout: Content Left | Image Right */}
                <div className="grid grid-cols-2 h-full overflow-hidden gap-6">
                    {/* Left Side - Content */}
                    <div className="h-full flex flex-col pr-4 overflow-y-auto">
                        <div className="flex flex-col space-y-4">
                            {/* Title */}
                            <div className="flex-shrink-0">
                                <Heading
                                    level={1}
                                    className="mb-3 text-5xl font-mono tracking-tight"
                                    style={{ color: colorTokens.oxfordBlue }}
                                >
                                    {recipe.name || recipe.title}
                                </Heading>
                                {/* Red dividing line */}
                                <div
                                    className="border-t mb-3"
                                    style={{ borderColor: colorTokens.red }}
                                ></div>
                                {/* Metadata */}
                                <div className="flex gap-4 text-sm font-mono uppercase tracking-wider opacity-60 mb-3">
                                    {formatCookingTime(recipe.cooking_time) && (
                                        <span>
                                            TIME: {formatCookingTime(recipe.cooking_time)}
                                        </span>
                                    )}
                                    {recipe.prep_time && (
                                        <span>
                                            PREP: {formatCookingTime(recipe.prep_time)}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Description/Summary - Compact */}
                            {(recipe.description || recipe.summary) && (
                                <div className="flex-shrink-0 pb-1 hairline-b">
                                    <Text
                                        size="base"
                                        className="opacity-80 line-clamp-2"
                                    >
                                        {recipe.description || recipe.summary}
                                    </Text>
                                </div>
                            )}

                            {/* Ingredients - Multi-column with expanded text */}
                            {ingredients.length > 0 && (
                                <div className="flex flex-col">
                                    <div
                                        className="border-t mb-3"
                                        style={{ borderColor: colorTokens.notecardBlue }}
                                    ></div>
                                    <Text
                                        size="lg"
                                        className="font-mono uppercase tracking-wider mb-3 opacity-60 block flex-shrink-0"
                                    >
                                        INGREDIENTS
                                    </Text>
                                    {combinedData ? (
                                        // Multi-component: Split into 2 columns, keeping component sections together
                                        (() => {
                                            // Group ingredients by component (header + its items)
                                            const componentGroups = [];
                                            let currentGroup = null;
                                            
                                            ingredients.forEach((item) => {
                                                if (item.type === 'header') {
                                                    // Save previous group if exists
                                                    if (currentGroup) {
                                                        componentGroups.push(currentGroup);
                                                    }
                                                    // Start new group
                                                    currentGroup = {
                                                        header: item,
                                                        items: []
                                                    };
                                                } else if (currentGroup) {
                                                    currentGroup.items.push(item);
                                                }
                                            });
                                            // Don't forget the last group
                                            if (currentGroup) {
                                                componentGroups.push(currentGroup);
                                            }
                                            
                                            // Distribute component groups to columns (keep groups together)
                                            const column1 = [];
                                            const column2 = [];
                                            let column1ItemCount = 0;
                                            let column2ItemCount = 0;
                                            
                                            componentGroups.forEach((group) => {
                                                const groupItemCount = group.items.length;
                                                
                                                // Choose column with fewer items, or column 1 if equal
                                                if (column1ItemCount <= column2ItemCount) {
                                                    column1.push(group.header);
                                                    group.items.forEach(item => column1.push(item));
                                                    column1ItemCount += groupItemCount;
                                                } else {
                                                    column2.push(group.header);
                                                    group.items.forEach(item => column2.push(item));
                                                    column2ItemCount += groupItemCount;
                                                }
                                            });
                                            
                                            return (
                                                <div className="grid grid-cols-2 gap-3">
                                                    <div className="flex flex-col gap-3">
                                                        {column1.map((item, index) => (
                                                            item.type === 'header' ? (
                                                                <Text
                                                                    key={`col1-${index}`}
                                                                    size="lg"
                                                                    className="font-mono font-medium opacity-100 leading-relaxed"
                                                                    style={{ color: colorTokens.oxfordBlue }}
                                                                >
                                                                    {item.text}
                                                                </Text>
                                                            ) : (
                                                                <Text
                                                                    key={`col1-${index}`}
                                                                    size="lg"
                                                                    className="opacity-80 line-clamp-3 leading-relaxed"
                                                                >
                                                                    {item.text}
                                                                </Text>
                                                            )
                                                        ))}
                                                    </div>
                                                    <div className="flex flex-col gap-3">
                                                        {column2.map((item, index) => (
                                                            item.type === 'header' ? (
                                                                <Text
                                                                    key={`col2-${index}`}
                                                                    size="lg"
                                                                    className="font-mono font-medium opacity-100 leading-relaxed"
                                                                    style={{ color: colorTokens.oxfordBlue }}
                                                                >
                                                                    {item.text}
                                                                </Text>
                                                            ) : (
                                                                <Text
                                                                    key={`col2-${index}`}
                                                                    size="lg"
                                                                    className="opacity-80 line-clamp-3 leading-relaxed"
                                                                >
                                                                    {item.text}
                                                                </Text>
                                                            )
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })()
                                    ) : (
                                        // Simple recipe: 2 columns
                                    <div
                                        className="grid gap-3"
                                        style={{
                                            gridTemplateColumns: `repeat(${ingredientColumns}, 1fr)`,
                                            gridAutoRows: 'min-content'
                                        }}
                                    >
                                        {ingredients.map((ingredient, index) => (
                                            <Text
                                                key={index}
                                                size="lg"
                                                className="opacity-80 line-clamp-3 leading-relaxed"
                                            >
                                                {typeof ingredient === 'string'
                                                    ? ingredient
                                                    : ingredient.name || ingredient}
                                            </Text>
                                        ))}
                                    </div>
                                    )}
                                </div>
                            )}

                            {/* Instructions - Multi-column with expanded text */}
                            {instructionSteps.length > 0 && (
                                <div className="flex flex-col">
                                    <div
                                        className="border-t mb-3"
                                        style={{ borderColor: colorTokens.notecardBlue }}
                                    ></div>
                                    <Text
                                        size="lg"
                                        className="font-mono uppercase tracking-wider mb-3 opacity-60 block flex-shrink-0"
                                    >
                                        INSTRUCTIONS
                                    </Text>
                                    <div
                                        className="grid gap-4"
                                        style={{
                                            gridTemplateColumns: `repeat(${instructionColumns}, 1fr)`,
                                            gridAutoRows: 'min-content'
                                        }}
                                    >
                                        {instructionSteps.map((step, index) => {
                                            // Handle multi-component format (with type field)
                                            if (step.type === 'header') {
                                                return (
                                                    <div key={index} className="col-span-full">
                                                        <Text
                                                            size="lg"
                                                            className="font-mono font-medium opacity-100 leading-relaxed"
                                                            style={{ color: colorTokens.oxfordBlue }}
                                                        >
                                                            {step.text}
                                                        </Text>
                                                    </div>
                                                );
                                            }
                                            
                                            // Count only item steps for numbering
                                            const stepNumber = instructionSteps
                                                .slice(0, index + 1)
                                                .filter(s => s.type === 'item' || !s.type)
                                                .length;
                                            
                                            return (
                                            <div key={index} className="flex gap-2">
                                                <span className="font-mono uppercase tracking-wider opacity-60 flex-shrink-0 text-lg">
                                                        {stepNumber}.
                                                </span>
                                                <Text
                                                    size="lg"
                                                    className="opacity-80 leading-relaxed"
                                                >
                                                        {step.type === 'item' 
                                                            ? step.text
                                                            : (typeof step === 'string'
                                                        ? step
                                                        : step.text ||
                                                          step.description ||
                                                                  step)}
                                                </Text>
                                            </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Bottom Row: Vibes, Servings, Difficulty */}
                            <div className="flex-shrink-0 pt-1 hairline-t">
                                {/* Vibes tags */}
                                {recipe.vibes && recipe.vibes.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mb-1">
                                        {recipe.vibes.map((vibe, index) => (
                                            <span
                                                key={index}
                                                className="font-mono text-xs uppercase tracking-wider bg-neutral-50 px-2 py-0.5 text-neutral-900 opacity-60"
                                            >
                                                {vibe}
                                            </span>
                                        ))}
                                    </div>
                                )}

                                {/* Additional Info */}
                                <div className="flex gap-4 text-sm font-mono uppercase tracking-wider opacity-60">
                                    {recipe.servings && (
                                        <span>SERVINGS: {recipe.servings}</span>
                                    )}
                                    {recipe.difficulty && (
                                        <span>DIFFICULTY: {recipe.difficulty}</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Side - Image */}
                    <div className="h-full bg-neutral-50 hairline flex items-center justify-center overflow-hidden">
                        <SignedImage
                            src={
                                recipe.images && recipe.images.length > 0
                                    ? recipe.images[0]
                                    : null
                            }
                            alt={recipe.name || recipe.title}
                            className="w-full h-full object-cover"
                            category={recipe.category}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RecipeCardOverlay;

